import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { AppLayout } from '../components/AppLayout';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import {
  ArrowLeft, Building2, Users, Target, FileSignature, CalendarClock, Paperclip,
  Loader2, Pencil, Save, X, Plus, Upload, FileText, Download, Trash2, MapPin, Phone, Boxes,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const inputCls = 'w-full h-10 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 text-sm';
const fmtEur = (v) => (v || v === 0) ? new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(v) + ' €' : '—';
const fmtDate = (v) => { if (!v) return '—'; try { const d = new Date(v); if (isNaN(d)) return v; return d.toLocaleDateString('fr-FR'); } catch { return v; } };
const STD_FIELDS = [
  { key: 'type', label: 'Type', type: 'select', options: ['Prospect', 'Client actif', 'Ancien client', 'Groupe / holding', 'Établissement / magasin', 'Compte archivé'] },
  { key: 'enseigne', label: "Nom de l'Enseigne GMS", type: 'text' },
  { key: 'gms', label: 'GMS', type: 'text' },
  { key: 'owner_name', label: 'Propriétaire du compte', type: 'text' },
  { key: 'address', label: 'Adresse de facturation', type: 'text' },
  { key: 'cp', label: 'Code postal', type: 'text' },
  { key: 'city', label: 'Ville', type: 'text' },
  { key: 'phone', label: 'Téléphone', type: 'text' },
  { key: 'surface_m2', label: 'M² du magasin', type: 'number' },
];

function EditField({ f, value, onChange }) {
  const set = (v) => onChange(f.key, v);
  if (f.type === 'textarea') return <textarea className={inputCls + ' h-20 py-2'} value={value || ''} onChange={(e) => set(e.target.value)} />;
  if (f.type === 'number') return <input type="number" className={inputCls} value={value ?? ''} onChange={(e) => set(e.target.value === '' ? null : Number(e.target.value))} />;
  if (f.type === 'date') return <input type="date" className={inputCls} value={value || ''} onChange={(e) => set(e.target.value)} />;
  if (f.type === 'checkbox') return <input type="checkbox" className="w-5 h-5" checked={!!value} onChange={(e) => set(e.target.checked)} />;
  if (f.type === 'select') return <select className={inputCls} value={value || ''} onChange={(e) => set(e.target.value)}><option value="">—</option>{(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}</select>;
  return <input className={inputCls} value={value || ''} onChange={(e) => set(e.target.value)} />;
}

function displayVal(f, value) {
  if (value === null || value === undefined || value === '') return '—';
  if (f.type === 'checkbox') return value ? 'Oui' : 'Non';
  if (f.type === 'number') return f.key.includes('ca_') || f.key.includes('estimatif') ? fmtEur(value) : new Intl.NumberFormat('fr-FR').format(value);
  if (f.type === 'date') return fmtDate(value);
  return String(value);
}

function DocRow({ d, token, user, onDelete }) {
  const canDelete = user?.role === 'admin' || user?.role === 'super_admin' || d.uploaded_by === user?.id;
  const dlUrl = `${BACKEND_URL}/api/capex-sales/documents/${d.id}/download?auth=${token}`;
  const fmtSize = (b) => !b ? '' : b > 1048576 ? (b / 1048576).toFixed(1) + ' Mo' : Math.ceil(b / 1024) + ' Ko';
  return (
    <div className="flex items-center gap-2 rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2 bg-white dark:bg-slate-800" data-testid={`ad-document-${d.id}`}>
      <FileText className="w-4 h-4 text-orange-500 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-slate-800 dark:text-white truncate">{d.original_filename}</div>
        <div className="text-[10px] text-slate-400">{fmtSize(d.size)}{d.related_type !== 'account' ? ` · ${d.related_type}` : ''}{d.uploaded_by_name ? ' · ' + d.uploaded_by_name : ''}</div>
      </div>
      <a href={dlUrl} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-orange-600" title="Télécharger"><Download className="w-4 h-4" /></a>
      {canDelete && <button onClick={() => onDelete(d)} className="text-slate-400 hover:text-red-600" title="Supprimer"><Trash2 className="w-4 h-4" /></button>}
    </div>
  );
}

export default function AccountDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api, token, user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('details');
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [note, setNote] = useState({ type: 'Compte-rendu', subject: '', body: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api().get(`/capex-sales/accounts/${id}/full`); setData(r.data); }
    catch (e) { toast.error('Compte introuvable'); } finally { setLoading(false); }
  }, [api, id]);
  useEffect(() => { load(); }, [load]);

  const acc = data?.account;
  const fields = data?.fields || [];
  const sections = [...new Set(fields.map((f) => f.section || 'Autres champs'))];

  const startEdit = () => { setForm({ ...acc, custom_fields: { ...(acc.custom_fields || {}) } }); setEditing(true); };
  const setStd = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const setCf = (k, v) => setForm((p) => ({ ...p, custom_fields: { ...(p.custom_fields || {}), [k]: v } }));
  const save = async () => {
    setSaving(true);
    try { await api().patch(`/capex-sales/accounts/${id}`, form); toast.success('Fiche enregistrée'); setEditing(false); load(); }
    catch (e) { toast.error('Erreur enregistrement'); } finally { setSaving(false); }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0]; e.target.value = '';
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error('Fichier trop volumineux (max 25 Mo)'); return; }
    const fd = new FormData(); fd.append('file', file);
    setUploading(true);
    try {
      await api().post('/capex-sales/documents/upload', fd, { params: { related_type: 'account', related_id: id, related_name: acc?.name || '' } });
      toast.success('Document ajouté'); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur d'upload"); } finally { setUploading(false); }
  };
  const delDoc = async (d) => { if (!window.confirm('Supprimer ce document ?')) return; try { await api().delete(`/capex-sales/documents/${d.id}`); toast.success('Supprimé'); load(); } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); } };

  const addNote = async () => {
    if (!note.subject.trim()) return toast.error('Objet requis');
    try {
      await api().post('/capex-sales/activities', { ...note, related_type: 'account', related_id: id, related_name: acc?.name, owner_name: user?.name });
      toast.success('Compte-rendu ajouté'); setNote({ type: 'Compte-rendu', subject: '', body: '' }); load();
    } catch (e) { toast.error('Erreur'); }
  };

  if (loading) return <AppLayout><div className="py-24 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto text-orange-500" /></div></AppLayout>;
  if (!acc) return <AppLayout><div className="py-24 text-center text-slate-400">Compte introuvable.</div></AppLayout>;

  const isClient = (acc.type || '').toLowerCase().includes('client');
  const TABS = [
    { key: 'details', label: 'Détails', Icon: Building2 },
    { key: 'contacts', label: `Contacts (${data.contacts.length})`, Icon: Users },
    { key: 'opportunities', label: `Opportunités (${data.opportunities.length})`, Icon: Target },
    { key: 'activities', label: `Comptes rendus (${data.activities.length})`, Icon: CalendarClock },
    { key: 'contracts', label: `Contrats (${data.contracts.length})`, Icon: FileSignature },
    { key: 'documents', label: `Fichiers (${data.documents.length})`, Icon: Paperclip },
  ];

  return (
    <AppLayout>
      <div className="space-y-4 animate-fade-in" data-testid="account-detail-page">
        <button onClick={() => navigate('/capex-sales')} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-orange-600" data-testid="ad-back-btn"><ArrowLeft className="w-4 h-4" /> Retour au CRM</button>

        {/* Header */}
        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 shadow-sm">
          <div className="flex items-start gap-4 flex-wrap">
            <span className="w-14 h-14 rounded-2xl grid place-items-center bg-orange-500/15 text-orange-600 flex-shrink-0"><Building2 className="w-7 h-7" /></span>
            <div className="flex-1 min-w-[220px]">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-2xl font-heading font-bold text-slate-900 dark:text-white">{acc.name}</h1>
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${isClient ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`} data-testid="ad-type-badge">{acc.type || 'Prospect'}</span>
              </div>
              <div className="text-sm text-slate-500 mt-1 flex items-center gap-3 flex-wrap">
                {acc.enseigne && <span className="inline-flex items-center gap-1"><Boxes className="w-3.5 h-3.5" /> {acc.enseigne}</span>}
                {acc.city && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {acc.city}{acc.cp ? ` (${acc.cp})` : ''}</span>}
                {acc.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {acc.phone}</span>}
                {acc.owner_name && <span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {acc.owner_name}</span>}
              </div>
            </div>
            <div className="flex gap-2">
              {!editing ? (
                <Button onClick={startEdit} className="gap-2 bg-orange-500 hover:bg-orange-600 text-white" data-testid="ad-edit-btn"><Pencil className="w-4 h-4" /> Modifier</Button>
              ) : (
                <>
                  <Button variant="outline" onClick={() => setEditing(false)}><X className="w-4 h-4" /></Button>
                  <Button onClick={save} disabled={saving} className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="ad-save-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Enregistrer</Button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-slate-200 dark:border-slate-800 overflow-x-auto">
          {TABS.map(({ key, label, Icon }) => (
            <button key={key} onClick={() => setTab(key)} data-testid={`ad-tab-${key}`}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${tab === key ? 'border-orange-500 text-orange-600' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>

        {/* DETAILS */}
        {tab === 'details' && (
          <div className="space-y-4">
            <Section title="Informations principales">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
                {STD_FIELDS.map((f) => (
                  <Row key={f.key} label={f.label} editing={editing}
                    display={f.key === 'surface_m2' && acc[f.key] != null ? new Intl.NumberFormat('fr-FR').format(acc[f.key]) : (acc[f.key] || '—')}
                    editor={<EditField f={f} value={form[f.key]} onChange={setStd} />} />
                ))}
              </div>
            </Section>
            {sections.map((sec) => {
              const secFields = fields.filter((f) => (f.section || 'Autres champs') === sec);
              return (
                <Section key={sec} title={sec}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
                    {secFields.map((f) => (
                      <Row key={f.id} label={f.label} editing={editing}
                        display={displayVal(f, (acc.custom_fields || {})[f.key])}
                        editor={<EditField f={f} value={(form.custom_fields || {})[f.key]} onChange={setCf} />} />
                    ))}
                  </div>
                </Section>
              );
            })}
          </div>
        )}

        {/* CONTACTS */}
        {tab === 'contacts' && (
          <div className="space-y-2">
            {data.contacts.length === 0 && <Empty text="Aucun contact rattaché." />}
            {data.contacts.map((c) => (
              <div key={c.id} className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-3 flex items-center gap-3" data-testid={`ad-contact-${c.id}`}>
                <Users className="w-5 h-5 text-blue-500" />
                <div className="flex-1"><div className="font-medium text-slate-800 dark:text-white">{[c.first_name, c.last_name].filter(Boolean).join(' ')}</div>
                  <div className="text-xs text-slate-500">{[c.role_title, c.commercial_role].filter(Boolean).join(' · ') || '—'}</div></div>
                <div className="text-xs text-slate-500 text-right">{c.email && <div>{c.email}</div>}{(c.mobile || c.phone) && <div>{c.mobile || c.phone}</div>}</div>
              </div>
            ))}
          </div>
        )}

        {/* OPPORTUNITIES */}
        {tab === 'opportunities' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {data.opportunities.length === 0 && <Empty text="Aucune opportunité." />}
            {data.opportunities.map((o) => (
              <div key={o.id} className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4" data-testid={`ad-opp-${o.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="font-semibold text-slate-800 dark:text-white">{o.name}</div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${o.status === 'open' ? 'bg-blue-100 text-blue-700' : o.stage === 'signe' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>{o.stage_label}</span>
                </div>
                <div className="text-xs text-slate-500 mt-2 flex items-center justify-between">
                  <span>CA : <b className="text-slate-700 dark:text-slate-200">{fmtEur(o.ca_annuel_previsionnel)}</b></span>
                  <span className="text-emerald-600">Prévi {fmtEur(o.forecast)}</span>
                </div>
                <div className="text-[11px] text-slate-400 mt-1">{o.probability}% · {o.owner_name || '—'}</div>
              </div>
            ))}
            <div><Button variant="outline" onClick={() => navigate('/capex-sales')} className="gap-1 text-sm"><Target className="w-4 h-4" /> Gérer dans le Pipeline</Button></div>
          </div>
        )}

        {/* ACTIVITIES / COMPTES RENDUS */}
        {tab === 'activities' && (
          <div className="space-y-4">
            <Section title="Nouveau compte-rendu / activité">
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                <select className={inputCls} value={note.type} onChange={(e) => setNote({ ...note, type: e.target.value })} data-testid="ad-note-type">
                  {(data.activity_types || ['Compte-rendu', 'Appel', 'RDV', 'Email', 'Note', 'Tâche']).map((t) => <option key={t}>{t}</option>)}
                </select>
                <input className={inputCls + ' sm:col-span-3'} placeholder="Objet…" value={note.subject} onChange={(e) => setNote({ ...note, subject: e.target.value })} data-testid="ad-note-subject" />
                <textarea className={inputCls + ' sm:col-span-4 h-20 py-2'} placeholder="Compte-rendu / détails…" value={note.body} onChange={(e) => setNote({ ...note, body: e.target.value })} data-testid="ad-note-body" />
              </div>
              <div className="flex justify-end mt-2"><Button onClick={addNote} className="gap-1 bg-orange-500 hover:bg-orange-600" data-testid="ad-note-save"><Plus className="w-4 h-4" /> Ajouter</Button></div>
            </Section>
            <div className="space-y-2">
              {data.activities.length === 0 && <Empty text="Aucune activité enregistrée." />}
              {data.activities.map((a) => (
                <div key={a.id} className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-3" data-testid={`ad-activity-${a.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2"><span className="text-[10px] px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 font-semibold">{a.type}</span>
                      <span className="font-medium text-slate-800 dark:text-white text-sm">{a.subject}</span></div>
                    <span className="text-[11px] text-slate-400">{fmtDate(a.created_at)}{a.owner_name ? ' · ' + a.owner_name : ''}</span>
                  </div>
                  {a.body && <p className="text-sm text-slate-600 dark:text-slate-300 mt-1.5 whitespace-pre-wrap">{a.body}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CONTRACTS */}
        {tab === 'contracts' && (
          <div className="space-y-2">
            {data.contracts.length === 0 && <Empty text="Aucun contrat." />}
            {data.contracts.map((c) => (
              <div key={c.id} className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-3 flex items-center gap-3" data-testid={`ad-contract-${c.id}`}>
                <FileSignature className="w-5 h-5 text-orange-500" />
                <div className="flex-1"><div className="font-medium text-slate-800 dark:text-white">{c.title}</div>
                  <div className="text-xs text-slate-500">{c.amount_ht ? fmtEur(c.amount_ht) : ''} {c.signed_date ? '· signé le ' + fmtDate(c.signed_date) : ''}</div></div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${c.status === 'Signé' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>{c.status}</span>
              </div>
            ))}
          </div>
        )}

        {/* DOCUMENTS */}
        {tab === 'documents' && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-500">Contrats, comptes rendus, plans… (PDF, images, Office · 25 Mo max)</p>
              <label className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white cursor-pointer" data-testid="ad-document-upload-btn">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Ajouter un fichier
                <input type="file" className="hidden" onChange={onUpload} disabled={uploading} accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.xls,.xlsx,.ppt,.pptx" data-testid="ad-document-file-input" />
              </label>
            </div>
            {data.documents.length === 0 ? <Empty text="Aucun document." /> : (
              <div className="space-y-1.5">{data.documents.map((d) => <DocRow key={d.id} d={d} token={token} user={user} onDelete={delDoc} />)}</div>
            )}
          </div>
        )}
      </div>
    </AppLayout>
  );
}

function Section({ title, children }) {
  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden shadow-sm">
      <div className="px-5 py-3 bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800">
        <h2 className="text-sm font-heading font-bold text-slate-700 dark:text-slate-200">{title}</h2>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function Row({ label, display, editor, editing }) {
  return (
    <div className="flex items-center justify-between gap-4 py-1 border-b border-slate-50 dark:border-slate-800/50">
      <span className="text-xs font-semibold text-slate-500 flex-shrink-0 w-1/2">{label}</span>
      {editing ? <div className="w-1/2">{editor}</div> : <span className="text-sm text-slate-800 dark:text-white text-right w-1/2 break-words">{display}</span>}
    </div>
  );
}

function Empty({ text }) {
  return <div className="rounded-xl border border-dashed border-slate-200 dark:border-slate-800 py-10 text-center text-sm text-slate-400">{text}</div>;
}
